import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PhoneInput } from './PhoneInput';
import { MessageInput } from './MessageInput';
import { formatPhoneForTel } from '@/lib/phone-utils';
import { isValidPhoneNumber, getCountryCallingCode, CountryCode } from 'libphonenumber-js';

interface SMSFormProps {
  contentData: Record<string, string>;
  onUpdate: (data: Record<string, string>) => void;
}

const STORAGE_KEY = 'qr-sms-draft';

export function SMSForm({ contentData, onUpdate }: SMSFormProps) {
  const [phone, setPhone] = useState(contentData.rawPhone || contentData.phone || '');
  const [countryCode, setCountryCode] = useState(contentData.countryCode || 'US');
  const [message, setMessage] = useState(contentData.message || '');

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.phone && !contentData.phone) setPhone(parsed.phone);
        if (parsed.countryCode) setCountryCode(parsed.countryCode);
        if (parsed.message && !contentData.message) setMessage(parsed.message);
      } catch {}
    }
  }, []);

  // Save to localStorage and update parent
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ phone, countryCode, message }));
    
    const fullPhone = formatPhoneForTel(phone, countryCode);
    onUpdate({
      phone: fullPhone,
      countryCode,
      message,
      rawPhone: phone,
    });
  }, [phone, countryCode, message, onUpdate]);

  // Check if phone is valid
  const isPhoneValid = phone.length > 0 && (() => {
    try {
      const dialCode = getCountryCallingCode(countryCode as CountryCode);
      return isValidPhoneNumber(`+${dialCode}${phone}`);
    } catch {
      return false;
    }
  })();

  const handleTestSMS = () => {
    if (!isPhoneValid) return;
    const fullPhone = formatPhoneForTel(phone, countryCode);
    const encodedMessage = encodeURIComponent(message);
    const url = message 
      ? `sms:${fullPhone}?body=${encodedMessage}`
      : `sms:${fullPhone}`;
    window.open(url, '_self');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
        SMS Message
      </h3>

      {/* Phone Input */}
      <PhoneInput
        value={phone}
        onChange={setPhone}
        countryCode={countryCode}
        onCountryChange={setCountryCode}
        label="Phone Number"
        required
        placeholder="Enter phone number"
      />

      {/* Message */}
      <MessageInput
        value={message}
        onChange={setMessage}
        label="Pre-filled Message (optional)"
        placeholder="Your message here..."
        maxLength={160}
        showPreview={false}
      />

      {/* Character info for SMS */}
      {message.length > 0 && (
        <div className="text-xs text-muted-foreground">
          {message.length <= 160 ? (
            <span className="text-primary">✓ Standard SMS (1 segment)</span>
          ) : message.length <= 306 ? (
            <span className="text-secondary-foreground">⚠ Long SMS (2 segments)</span>
          ) : (
            <span className="text-destructive">⚠ Very long ({Math.ceil(message.length / 153)} segments)</span>
          )}
        </div>
      )}

      {/* Test SMS Button */}
      <div className="pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleTestSMS}
          disabled={!isPhoneValid}
          className="w-full gap-2"
        >
          <MessageSquare className="h-4 w-4" />
          Test SMS Link
          <ExternalLink className="h-3 w-3 ml-auto" />
        </Button>
        {!isPhoneValid && phone.length > 0 && (
          <p className="text-xs text-muted-foreground mt-2 text-center">
            Enter a valid phone number to test
          </p>
        )}
      </div>

      {/* Info */}
      <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
        <p className="font-medium mb-1">💬 How it works:</p>
        <p>
          When someone scans this QR code, it will open their messaging app with
          the number and message ready to send.
        </p>
      </div>
    </motion.div>
  );
}
